import type { Element, ElementContent, Root, RootContent } from 'hast';

declare module 'hast' {
  interface ElementData {
    /** The ordinal of a marker this plugin made, or `?` for an unverified claim. */
    citationMarker?: string;
    /** On the span this plugin wraps a claim's sentence in: the markers that close it. */
    citedClaim?: string[];
    /** Markers with nothing but space between them, `[[1]] [[2]][[3]]`, drawn as one (#5988). */
    citationCluster?: string[];
  }
}

/**
 * The markers the API emits. `[[n]]` attributes a claim to source n; `[[?]]` says the server
 * checked a claim against the span it cited and would not stand behind the attribution.
 *
 * Both are `[[...]]` rather than `[...]` because assistant answers are dense with markdown
 * links, and a marker sharing bracket syntax with `[text](url)` gets parsed as one.
 */
const CITATION_MARKER = /\[\[(\d{1,3}|\?)\]\]/g;

/** Text in these is quoted, not prose, so `[[1]]` in it is what the reader should see. */
const QUOTED = new Set(['code', 'pre']);

const markerElement = (ordinal: string): Element => ({
  type: 'element',
  tagName: 'sup',
  properties: {},
  children: [{ type: 'text', value: ordinal }],
  data: { citationMarker: ordinal },
});

/** `value` split into its text and the markers in it. */
const splitText = (value: string): ElementContent[] => {
  const parts: ElementContent[] = [];
  let last = 0;
  for (const match of value.matchAll(CITATION_MARKER)) {
    if (match.index > last) parts.push({ type: 'text', value: value.slice(last, match.index) });
    parts.push(markerElement(match[1]));
    last = match.index + match[0].length;
  }
  if (last === 0) return [{ type: 'text', value }];
  if (last < value.length) parts.push({ type: 'text', value: value.slice(last) });
  return parts;
};

/** Takes the markers out of a link's text, and returns them. */
const liftFromLink = (node: Element): Element[] => {
  const lifted: Element[] = [];
  const visit = (parent: Element) => {
    parent.children = parent.children.flatMap((child): ElementContent[] => {
      if (child.type === 'text') {
        const parts = splitText(child.value);
        lifted.push(...parts.filter((part): part is Element => part.type === 'element'));
        return parts.filter((part) => part.type === 'text');
      }
      if (child.type === 'element' && !QUOTED.has(child.tagName)) visit(child);
      return [child];
    });
  };
  visit(node);
  return lifted;
};

/**
 * Where the last sentence in `value` begins: just past its last full stop, question or
 * exclamation mark (and any closing quote or bracket) that whitespace follows. -1 for none.
 */
const lastSentenceStart = (value: string) => {
  let start = -1;
  for (const match of value.matchAll(/[.!?]+["'’”)\]]*\s+/g)) {
    start = match.index + match[0].length;
  }
  return start;
};

const isMarker = (node: ElementContent | undefined): node is Element =>
  node?.type === 'element' && node.data?.citationMarker !== undefined;

const text = (value: string): ElementContent => ({ type: 'text', value });

/**
 * Wraps the sentence each marker closes in a `span` that names the markers in
 * `data.citedClaim`, so `CitedAnswer` can show which words an unverified mark is about.
 *
 * A sentence runs back from its marker to the nearest of: an earlier marker, a line break,
 * a sentence boundary in the text, or the start of its parent. It is found among one
 * parent's children, and an element among them - a link, a code span, emphasis - is taken
 * whole or not at all, so the span can never open inside a link or code and close outside
 * it. Markers in a row, `[[1]][[?]]`, close one sentence together.
 */
const wrapClaims = (children: ElementContent[]): ElementContent[] => {
  const out: ElementContent[] = [];
  const claims = new Map<Element, Element>();

  for (const child of children) {
    if (!isMarker(child)) {
      out.push(child);
      continue;
    }

    // The space between a sentence and its marker belongs to neither.
    const between: ElementContent[] = [];
    let last = out.at(-1);
    while (last?.type === 'text') {
      const trimmed = last.value.trimEnd();
      if (trimmed !== last.value) between.unshift(text(last.value.slice(trimmed.length)));
      out.pop();
      if (trimmed) {
        out.push(text(trimmed));
        break;
      }
      last = out.at(-1);
    }

    const previous = out.at(-1);
    if (isMarker(previous)) {
      const claim = claims.get(previous);
      claim?.data?.citedClaim?.push(child.data!.citationMarker!);
      if (claim) claims.set(child, claim);
      out.push(...between, child);
      continue;
    }

    const sentence: ElementContent[] = [];
    while (out.length > 0) {
      const node = out.at(-1)!;
      if (node.type === 'element' && (node.tagName === 'br' || isMarker(node))) break;
      if (node.type === 'text') {
        const start = lastSentenceStart(node.value);
        if (start >= 0) {
          out[out.length - 1] = text(node.value.slice(0, start));
          if (start < node.value.length) sentence.unshift(text(node.value.slice(start)));
          break;
        }
      }
      sentence.unshift(out.pop()!);
    }

    // Leading space stays outside, where the sentence before it left off.
    const first = sentence[0];
    if (first?.type === 'text') {
      const trimmed = first.value.trimStart();
      if (trimmed !== first.value) out.push(text(first.value.slice(0, first.value.length - trimmed.length)));
      if (trimmed) sentence[0] = text(trimmed);
      else sentence.shift();
    }

    if (sentence.length > 0) {
      const claim: Element = {
        type: 'element',
        tagName: 'span',
        properties: {},
        children: sentence,
        data: { citedClaim: [child.data!.citationMarker!] },
      };
      claims.set(child, claim);
      out.push(claim);
    }
    out.push(...between, child);
  }

  return out;
};

const isSpace = (node: ElementContent | undefined) => node?.type === 'text' && node.value.trim() === '';

/**
 * Joins each run of markers that only space separates into one `sup` that names them all in
 * `data.citationCluster`, so `CitedAnswer` draws `[[1]][[2]][[3]]` as one "1, 2, 3" rather than
 * three chips in a row (#5988). The space between them goes; the order is the server's.
 */
const clusterMarkers = (children: ElementContent[]): ElementContent[] => {
  const out: ElementContent[] = [];
  for (let index = 0; index < children.length; index++) {
    const child = children[index];
    if (!isMarker(child)) {
      out.push(child);
      continue;
    }
    const run = [child.data!.citationMarker!];
    let next = index + 1;
    while (isSpace(children[next])) next++;
    while (isMarker(children[next])) {
      run.push((children[next] as Element).data!.citationMarker!);
      index = next;
      next++;
      while (isSpace(children[next])) next++;
    }
    out.push(run.length === 1 ? child : {
      type: 'element',
      tagName: 'sup',
      properties: {},
      children: [text(run.join(', '))],
      data: { citationCluster: run },
    });
  }
  return out;
};

const withMarkers = (children: ElementContent[]): ElementContent[] =>
  clusterMarkers(wrapClaims(children.flatMap((child) => {
    if (child.type === 'text') return splitText(child.value);
    if (child.type !== 'element' || QUOTED.has(child.tagName)) return [child];
    // A button inside an anchor is invalid, and assistive technology reads it as one control.
    // The marker still belongs to the claim, so it moves to just after the link.
    if (child.tagName === 'a') return [child, ...liftFromLink(child)];
    child.children = withMarkers(child.children);
    return [child];
  })));

/**
 * Turns each citation marker in an answer into a `sup` that carries its ordinal in
 * `data.citationMarker`, for `CitedAnswer`'s `sup` override to render.
 *
 * It works on the parsed tree rather than the raw string, so a marker in a code span or
 * block stays the text it is, and one inside link text does not end up inside the anchor.
 * It runs after sanitization, and `data` is not something markdown or HTML can set, so the
 * content cannot forge a marker the server never checked: a literal
 * `<sup class="assistant-citation-marker">1</sup>` is just text.
 */
const rehypeCitationMarkers = () => (tree: Root) => {
  tree.children = tree.children.flatMap((child): RootContent[] =>
    child.type === 'doctype' ? [child] : withMarkers([child]));
};

export default rehypeCitationMarkers;
